import React, { useCallback, useMemo, useState } from 'react'
import { Button, Col, Container, Input, ListGroup, ListGroupItem, Row, Spinner } from 'reactstrap'
import UserEditModal from './UserEditModal'
import { useDeleteUserMutation, useGetAllUsersQuery, useReactivateUserMutation } from '../../backoffice/apis/userApi'
import { useTranslation } from 'react-i18next'
import { user } from '../../backoffice/dtos/authentication/user'
import { Loading } from '../views/Loading'
import { toast } from 'react-toastify'
import { confirmAlert } from 'react-confirm-alert'

export function Users(
    {
        shown
    }: {
        shown: Boolean
    }
){
    const [loading, setLoading] = useState(false)
    const [modalData, setModalData] = useState<{isOpen: boolean, user: user | undefined}>({isOpen: false, user: undefined})
    const [searchText, setSearchText] = useState<string>()
    const {data: users} = useGetAllUsersQuery(undefined, {skip: !shown, refetchOnMountOrArgChange: true})
    const [ deactivateUserApi ] = useDeleteUserMutation()
    const [ activateUserApi ] = useReactivateUserMutation()

    const {t} = useTranslation(['user_info','common'])

    const filteredData = useMemo(() => {
        if (searchText === undefined) {
            return users;
        }
        if (users === undefined) {
            return undefined;
        }
        return users.filter(
            (it) => {
                return it.userName.includes(searchText)
                    || it.email.includes(searchText)
            }
        )
    }, [users, searchText])

    const open = useCallback((user: user | undefined) => {
        setModalData({isOpen: true, user: user});
    }, []);

    const close = useCallback(() => {
        setModalData({isOpen: false, user: undefined});
    }, []);

    const handleChange = ({
        target: { value },
    }: React.ChangeEvent<HTMLInputElement>) => {
        if (value.length === 0) {
            setSearchText(undefined);
        } else {
            setSearchText(value)
        }
    }

    const deactivateUser = useCallback(async (
        userId: number
    ) => {
        try {
            setLoading(true)
            await deactivateUserApi(userId)
        } catch(error) {
            toast("Απέτυχε η απενεργοποίηση του χρήστη");
        } finally {
            setLoading(false)
        }
    }, [])

    const activateUser = useCallback(async (
        userId: number
    ) => {
        try {
            setLoading(true)
            await activateUserApi(userId)
        } catch(error) {
            toast("Απέτυχε η ενεργοποίηση του χρήστη");
        } finally {
            setLoading(false)
        }
    }, [])

    const confirmUserActivation = useCallback((user: user) => {
        confirmAlert({
            title: t("Activate user"),
            message: t("Are you sure you want to activate the user {{username}}?", { username: user.userName }),
            buttons: [{ label: t('yes', { ns:'common' }), onClick: () => activateUser(user.id)}, { label: t('no', { ns: 'common' }) }]
        })
    }, [activateUser]);

    const confirmUserDeactivation = useCallback((user: user) => {
        confirmAlert({
            title: t("Deactivate user"),
            message: t("Are you sure you want to deactivate the user {{username}}?", { username: user.userName }),
            buttons: [{ label: t('yes', { ns:'common' }), onClick: () => deactivateUser(user.id)}, { label: t('no', { ns: 'common' }) }]
        })
    }, [deactivateUser]);

    const getActiveButton = useCallback((user: user) => {
        if (user.isDeleted) {
            return (
                <Col sm="auto" className='d-flex justify-content-end'>
                    <Button color="success" onClick={() => confirmUserActivation(user)} disabled={loading}>
                        <i className='fa  fa-check fa-lg me-2'></i>{t('Activate', { ns: 'common' })}{loading && <Spinner size='sm' className='ms-2'/> }
                    </Button>
                </Col>
            );
        } else {
            return (
                <Col sm="auto" className='d-flex justify-content-end'>
                    <Button color="danger" onClick={() => confirmUserDeactivation(user)} disabled={loading}>
                        <i className='fa  fa-trash fa-lg me-2'></i>{t('Deactivate', { ns: 'common' })}{loading && <Spinner size='sm' className='ms-2'/> }
                    </Button>
                </Col>
            );
        }
    }, [loading, confirmUserActivation, ])

    return (
        <Container className='my-2'>
            <Row>
                <Col>
                    <Input value={searchText} onChange={handleChange} placeholder={t('search', { ns: 'common' })}/>
                </Col>
                <Col sm="auto">
                    <Button color='primary' onClick={() => open(undefined)}>
                        <span className='fa fa-lg fa-plus me-2' />{t('Add', { ns: 'common' })}
                    </Button>
                </Col>
            </Row>
            <ListGroup className='mt-4'>
                <ListGroupItem >
                    <Row >
                        <Col lg={2}><h5> {t('username')} </h5></Col>
                        <Col lg={3}><h5> E-mail </h5> </Col>
                        <Col lg={2}></Col>
                    </Row>
                </ListGroupItem>
                {
                    filteredData ? filteredData.map(
                        (user, index) => (
                            <ListGroupItem key={index} >
                                <Row  divider>
                                    <Col lg={2} className='bold-text d-flex flex-column justify-content-center'>{user.userName}</Col>
                                    <Col lg={3} className="d-flex flex-column justify-content-center">{user.email}</Col>
                                    <Col className='d-flex justify-content-end'>
                                        <Button color="primary " className='w-40 ' onClick={() => open(user)} disabled={loading}>
                                            <i className='fa fa-edit fa-lg'></i>{loading && <Spinner size='sm' className='ms-2'/> }
                                        </Button>
                                    </Col>
                                    {getActiveButton(user)}
                                </Row>
                            </ListGroupItem>
                        )
                    ) : (
                        <Loading className="m-2"/>
                    )
                }
            </ListGroup>
            {
                modalData.isOpen &&
                    <UserEditModal isOpen={modalData.isOpen} close={close} userId={modalData.user?.id} />
            }
        </Container>
    )
}
